import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Checkbox, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { STATE_FILTER_OPTIONS } from '../lib/us-states';

/**
 * JurisdictionCreateModal — bespoke jurisdiction create flow (Government
 * data-model §2.2). Unlike the generic CreateEntityModal, creating a jurisdiction
 * REQUIRES attaching an assessor AND a collector, each chosen from a searchable
 * dropdown FILTERED by the jurisdiction's state and selected INDEPENDENTLY
 * (picking an assessor never auto-picks a collector).
 *
 *   assessor options  ← GET /api/assessors-by-state/:state
 *   collector options ← GET /api/collectors-by-state/:state
 *   create            → POST /api/jurisdictions { …, assessor_id, collector_id }
 *
 * Both dropdowns stay disabled until a state is chosen; changing the state clears
 * any prior assessor/collector selection (their option sets no longer apply).
 */

export interface JurisdictionCreateModalProps {
  open: boolean;
  onClose: () => void;
  invalidateKeys?: string[][];
  /** Pre-fill when opened from a context that already knows them (the property
   *  wizard: the state and the assessor just chosen, the collector just created). */
  defaults?: { state?: string; assessorId?: string; collectorId?: string };
  /** Called after a successful create with the new id and the picks it was made from,
   *  so the opener can select them (the wizard auto-picks the collector and jurisdiction). */
  onCreated?: (created: { id: string; assessorId: string; collectorId: string }) => void;
}

interface GovEntity {
  id: string;
  name: string;
  short_name?: string | null;
}

const labelStyle = {
  display: 'block',
  marginBottom: 'var(--sp-1)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)',
  color: 'var(--color-ink)',
} as const;

const requiredMark = <span style={{ color: 'var(--color-status-red)' }}> *</span>;

export function JurisdictionCreateModal({ open, onClose, invalidateKeys = [], defaults, onCreated }: JurisdictionCreateModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [shortName, setShortName] = useState('');
  const [state, setState] = useState('');
  const [type, setType] = useState('');
  const [code, setCode] = useState('');
  const [directAssessment, setDirectAssessment] = useState(false);
  const [assessorId, setAssessorId] = useState('');
  const [collectorId, setCollectorId] = useState('');

  // Reset the whole form each time the modal opens (to the opener's defaults, if any).
  useEffect(() => {
    if (open) {
      setName(''); setTitle(''); setShortName(''); setState(defaults?.state ?? '');
      setType(''); setCode(''); setDirectAssessment(false);
      setAssessorId(defaults?.assessorId ?? ''); setCollectorId(defaults?.collectorId ?? '');
    }
    // defaults is read only at open time, by design: a parent re-render must not wipe typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Changing state invalidates prior assessor/collector picks (they were scoped
  // to the old state's option set).
  function handleStateChange(next: string) {
    setState(next);
    setAssessorId('');
    setCollectorId('');
  }

  const assessorsQuery = useQuery<GovEntity[]>({
    queryKey: ['assessors-by-state', state],
    queryFn: ({ signal }) => api.get<GovEntity[]>(`/api/assessors-by-state/${state}`, signal),
    enabled: open && !!state,
  });
  const collectorsQuery = useQuery<GovEntity[]>({
    queryKey: ['collectors-by-state', state],
    queryFn: ({ signal }) => api.get<GovEntity[]>(`/api/collectors-by-state/${state}`, signal),
    enabled: open && !!state,
  });

  const assessorOptions = useMemo(
    () => (assessorsQuery.data ?? []).map((a) => ({ value: a.id, label: a.short_name ? `${a.name} (${a.short_name})` : a.name })),
    [assessorsQuery.data],
  );
  const collectorOptions = useMemo(
    () => (collectorsQuery.data ?? []).map((c) => ({ value: c.id, label: c.short_name ? `${c.name} (${c.short_name})` : c.name })),
    [collectorsQuery.data],
  );

  const canSubmit =
    name.trim() !== '' && title.trim() !== '' && shortName.trim() !== '' &&
    state !== '' && assessorId !== '' && collectorId !== '';

  const create = useMutation<{ id: string }, Error, void>({
    mutationFn: () => api.post<{ id: string }>('/api/jurisdictions', {
      name: name.trim(),
      title: title.trim(),
      short_name: shortName.trim(),
      state,
      type: type.trim() || null,
      code: code.trim() || null,
      direct_assessment: directAssessment,
      assessor_id: assessorId,
      collector_id: collectorId,
    }),
    onSuccess: (created) => {
      toast({ variant: 'success', message: 'Created jurisdiction.' });
      for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key });
      onCreated?.({ id: created.id, assessorId, collectorId });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit && !create.isPending) create.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!create.isPending) onClose(); }}
      title="New jurisdiction"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!canSubmit} onClick={() => create.mutate()}>
            Create jurisdiction
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
          <label>
            <span style={labelStyle}>Name{requiredMark}</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            <span style={labelStyle}>Title{requiredMark}</span>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>
          <label>
            <span style={labelStyle}>Short name{requiredMark}</span>
            <Input value={shortName} onChange={(e) => setShortName(e.target.value)} required />
          </label>
          <label>
            <span style={labelStyle}>State{requiredMark}</span>
            <Select
              value={state}
              onChange={(e) => handleStateChange(e.target.value)}
              options={STATE_FILTER_OPTIONS}
              placeholder="Select a state…"
            />
          </label>
          <label>
            <span style={labelStyle}>Type</span>
            <Input value={type} onChange={(e) => setType(e.target.value)} placeholder="e.g. County" />
          </label>
          <label>
            <span style={labelStyle}>Code</span>
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
        </div>

        <p style={{
          marginTop: 'var(--sp-5)', marginBottom: 'var(--sp-2)',
          fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)',
        }}>
          {state
            ? `Assessor and collector are filtered by ${state} and selected independently.`
            : 'Choose a state to load its assessors and collectors.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
          <div>
            <span style={labelStyle}>Assessor{requiredMark}</span>
            <SearchableSelect
              ariaLabel="Assessor"
              disabled={!state}
              loading={assessorsQuery.isFetching}
              options={assessorOptions}
              value={assessorId}
              onChange={setAssessorId}
              placeholder={state ? 'Search assessors…' : 'Select a state first'}
              emptyText={`No assessors in ${state || 'this state'}`}
            />
          </div>
          <div>
            <span style={labelStyle}>Collector{requiredMark}</span>
            <SearchableSelect
              ariaLabel="Collector"
              disabled={!state}
              loading={collectorsQuery.isFetching}
              options={collectorOptions}
              value={collectorId}
              onChange={setCollectorId}
              placeholder={state ? 'Search collectors…' : 'Select a state first'}
              emptyText={`No collectors in ${state || 'this state'}`}
            />
          </div>
        </div>

        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Checkbox
            label="Direct assessment jurisdiction"
            checked={directAssessment}
            onChange={(e) => setDirectAssessment(e.target.checked)}
          />
        </div>
      </form>
    </Modal>
  );
}

interface SearchableSelectProps {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * SearchableSelect — a minimal filter-as-you-type dropdown. Local to the
 * jurisdiction create flow: an input filters the option list; clicking an option
 * commits it. No external combobox dependency.
 */
function SearchableSelect({
  options, value, onChange, ariaLabel, placeholder, emptyText, disabled, loading,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  // When closed, the input shows the selected label; when open, the live query.
  const inputValue = open ? query : (selected?.label ?? '');

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Input
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        disabled={disabled}
        placeholder={loading ? 'Loading…' : placeholder}
        value={inputValue}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
      />
      {open && !disabled && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 10,
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            maxHeight: 220,
            overflowY: 'auto',
            margin: 0,
            padding: 'var(--sp-1)',
            listStyle: 'none',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--sh-modal)',
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: 'var(--sp-2) var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              {loading ? 'Loading…' : emptyText}
            </li>
          ) : (
            filtered.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    fontSize: 'var(--fs-md)',
                    padding: 'var(--sp-2) var(--sp-3)',
                    border: 'none',
                    borderRadius: 'var(--r-sm)',
                    background: o.value === value ? 'var(--brand-primary-bg)' : 'transparent',
                    color: 'var(--color-ink)',
                    cursor: 'pointer',
                  }}
                >
                  {o.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
